import { pageURLs } from "@/constants";
import { queueGuide } from "@/components/GuidedSpotlight";
import { startTogether } from "@/components/MemberTour/together";
import { tourLengthTitle } from "@/components/MemberTour/steps";

const wantAdPath = (wantAdId) => `${pageURLs.wantAdDetails}/${wantAdId}`;

export const SETUP_GUIDE_OPEN_EVENT = "setupGuide:open";
export const MEMBER_TOUR_START_EVENT = "memberTour:start";

export const openSetupGuide = () => window.dispatchEvent(new Event(SETUP_GUIDE_OPEN_EVENT));

export const startMemberTour = (options) =>
  window.dispatchEvent(
    new CustomEvent(MEMBER_TOUR_START_EVENT, { detail: { resume: Boolean(options?.resume) } }),
  );

const SELL_FIRST = ["tour", "addProperty", "seeDemand"];

export const orderForFocus = (steps, focus) =>
  focus === "sell"
    ? [
        ...SELL_FIRST.map((id) => steps.find((step) => step.id === id)).filter(Boolean),
        ...steps.filter((step) => !SELL_FIRST.includes(step.id)),
      ]
    : steps;

export const runStepAction = (action, navigate) => {
  if (action.tour) {
    startMemberTour({ resume: action.resume });
    return;
  }
  if (action.together) {
    action.together(navigate);
    return;
  }
  if (action.guide) queueGuide(action.guide);
  window.scrollTo({ top: 0 });
  navigate(action.to);
};

export const STEP_COPY = {
  tour: {
    title: tourLengthTitle(),
    doneLine: "Tour done. Replay it any time from here.",
    outcome:
      "See how the Buyer Listing Service connects Buyer Want-Listings with active for-sale listings across your MLS/association network.",
    action: () => ({ label: "Play", tour: true }),
  },
  wantListing: {
    title: "Publish your first Buyer Want‑Listing",
    doneLine: "Your first Buyer Want-Listing is live.",
    outcome:
      "Describe the property, not the person. No client information is ever shared. We match it against active and coming-soon for-sale listings in the MLS and alert you when one fits.",
    action: () => ({ label: "Do it together", together: startTogether }),
  },
  seeMatches: {
    title: "See the properties that fit",
    doneLine: "You've seen your matches.",
    outcome:
      "Every active and coming-soon for-sale listing that fits your Buyer Want-Listing, scored, with the reasons it fits.",
    action: (step) =>
      step.wantAdId
        ? {
            label: "Show me",
            to: `${wantAdPath(step.wantAdId)}?tabKey=Matches`,
            guide: {
              path: wantAdPath(step.wantAdId),
              target: '.tab-wrap [data-rr-ui-event-key="Matches"]',
              title: "Your matches live here",
              body: "Each property that fits shows its score and why it fits. New ones arrive as for-sale listings hit the MLS.",
            },
          }
        : { note: "Once one is posted" },
  },
  shareToGitcha: {
    title: "Share a Buyer Want‑Listing beyond your network",
    doneLine: "Your Buyer Want-Listing is on Gitcha.com.",
    outcome:
      "Syndicate to the public via Gitcha so agents and consumers outside your MLS/association network can see it. No client information is ever shared.",
    action: (step) =>
      step.wantAdId
        ? {
            label: "Show me",
            to: wantAdPath(step.wantAdId),
            guide: {
              path: wantAdPath(step.wantAdId),
              target: ".gitcha-share__btn",
              title: "Syndicate to the public via Gitcha",
              body: "One tap puts this Buyer Want-Listing on Gitcha.com. No client information is ever shared.",
            },
          }
        : { note: "Once one is posted" },
  },
  addProperty: {
    title: "Store a prospect's property",
    doneLine: "Your first prospect's property is in My Properties.",
    outcome:
      "Store a property you don't have listed yet and get demand insights for it, for pricing, marketing and listing strategy conversations.",
    action: () => ({ label: "Add one", to: pageURLs.agentCreateInventory }),
  },
  seeDemand: {
    title: "Get demand insights for a property",
    doneLine: "You've seen demand insights for a property.",
    outcome:
      "How many Buyer Want-Listings match it, and at what budgets. Bring those insights to your next listing appointment.",
    action: (step) =>
      step.propertyId
        ? {
            label: "Show me",
            to: `${pageURLs.home}?selectInvId=${encodeURIComponent(step.propertyId)}`,
            guide: {
              path: pageURLs.home,
              target: "[data-member-tour='sell-demand']",
              title: "Your demand insights",
              body: "How many Buyer Want-Listings match this property and where their budgets cluster. Drag the slider to test a price, then download the Buyer Match Report.",
            },
          }
        : { note: "Once one is added" },
  },
  photoAndBio: {
    title: "Add your photo and bio",
    doneLine: "Your photo and bio are live.",
    outcome:
      "Your photo shows on your Buyer Want-Listings, and your Gitcha bio shows who you are to other agents and the public.",
    action: () => ({
      label: "Show me",
      to: `${pageURLs.settings}?tab=profile`,
      guide: {
        path: pageURLs.settings,
        target: ".bio-studio__sections .bio-studio__card",
        title: "Your photo and bio live here",
        body: "Add a photo, then a few sentences about where you work and who you help. Save once and it shows everywhere.",
      },
    }),
  },
  introduction: {
    title: "Send an introduction or a\u00a0message",
    doneLine: "You've reached another agent. Either one counts.",
    outcome:
      "Open a Buyer Want-Listing from another agent and either message them with a question or send your active for-sale listing as an introduction. Either one counts.",
    action: () => ({
      label: "Show me",
      to: pageURLs.home,
      guide: {
        path: pageURLs.home,
        target: ".property-listing-outer",
        title: "Buyer Want-Listings from other agents",
        body: "Open one to message the agent behind it, or send your active for-sale listing as an introduction from its page.",
      },
    }),
  },
};

export const copyForStep = (step) => STEP_COPY[step?.id];
